import { useCallback, useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { AssetModelsSelectorDocument } from 'generated/resources/graphql'

interface AssetModel {
  id: string
  brand: string
  name: string
  typeName: string
}

interface UseAllAssetModelsResult {
  data: AssetModel[]
  loading: boolean
  error: Error | undefined
}

const PAGE_SIZE = 20

export const useAllAssetModels = (): UseAllAssetModelsResult => {
  const [allData, setAllData] = useState<AssetModel[]>([])
  const [isLoadingAll, setIsLoadingAll] = useState(true)
  const [fetchError, setFetchError] = useState<Error | undefined>()

  const { data, loading, error, fetchMore } = useQuery(AssetModelsSelectorDocument, {
    variables: {
      first: PAGE_SIZE,
      after: null,
    },
    notifyOnNetworkStatusChange: true,
  })

  const fetchAllPages = useCallback(async () => {
    if (!data?.itManagement?.itAssetModelsConnection) {
      return
    }

    try {
      setIsLoadingAll(true)
      const initialEdges = data.itManagement.itAssetModelsConnection.edges || []
      let accumulated = initialEdges.map((edge) => edge.node)
      let hasNextPage = data.itManagement.itAssetModelsConnection.pageInfo.hasNextPage
      let endCursor = data.itManagement.itAssetModelsConnection.pageInfo.endCursor

      while (hasNextPage && endCursor) {
        const result = await fetchMore({
          variables: {
            first: PAGE_SIZE,
            after: endCursor,
          },
        })

        const newEdges = result.data?.itManagement?.itAssetModelsConnection?.edges || []
        const newNodes = newEdges.map((edge) => edge.node)
        accumulated = [...accumulated, ...newNodes]

        hasNextPage =
          result.data?.itManagement?.itAssetModelsConnection?.pageInfo.hasNextPage || false
        endCursor = result.data?.itManagement?.itAssetModelsConnection?.pageInfo.endCursor || null
      }

      setAllData(accumulated)
      setFetchError(undefined)
    } catch (err) {
      setFetchError(err instanceof Error ? err : new Error('Failed to fetch asset models'))
    } finally {
      setIsLoadingAll(false)
    }
  }, [data, fetchMore])

  useEffect(() => {
    if (!loading && data && !error) {
      fetchAllPages()
    } else if (error) {
      setFetchError(error)
      setIsLoadingAll(false)
    }
  }, [data, loading, error, fetchAllPages])

  return {
    data: allData,
    loading: loading || isLoadingAll,
    error: fetchError,
  }
}
